import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaUrl } from '../../config/api';
import { Dialog, IconButton } from '../../ui';
import { ChevronLeftIcon, ChevronRightIcon } from '../../ui/icons';

const SWIPE_PX = 50;

/**
 * Every image of an item, one at a time (spec §1.5): a Dialog (focus kept
 * inside, Esc closes, focus back on the thumbnail that opened it), the
 * `original` variant fitted to the screen, ‹ › and ← → to move, "n / N",
 * the field's name as caption, swipe on touch screens. Images load when
 * shown. images: [{id, caption}].
 */
function Lightbox({ images, index, onIndex, onClose, title }) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState({});
  const touch = useRef(null);
  const count = images.length;
  const image = images[index];
  const go = (delta) => onIndex((index + delta + count) % count);

  useEffect(() => {
    const onKey = (event) => {
      if (count < 2 || event.defaultPrevented) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); onIndex((index - 1 + count) % count); }
      if (event.key === 'ArrowRight') { event.preventDefault(); onIndex((index + 1) % count); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, count, onIndex]);

  if (!image) return null;
  return (
    <Dialog open onClose={onClose} title={title} size="lg" className="lightbox">
      <figure className="lightbox__figure"
              onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touch.current === null || count < 2) return;
                const dx = e.changedTouches[0].clientX - touch.current;
                touch.current = null;
                if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
              }}>
        <div className={`lightbox__frame${loaded[image.id] ? '' : ' is-loading'}`}>
          <img key={image.id} src={mediaUrl(image.id, 'original')} alt={image.caption || ''}
               onLoad={() => setLoaded((all) => ({ ...all, [image.id]: true }))} />
        </div>
        <figcaption className="lightbox__caption">
          {image.caption && <span>{image.caption}</span>}
          {count > 1 && <span className="lightbox__count">{t('item.lightbox.position', { n: index + 1, total: count })}</span>}
        </figcaption>
      </figure>
      {count > 1 && (
        <div className="lightbox__nav">
          <IconButton icon={ChevronLeftIcon} label={t('item.lightbox.previous')} onClick={() => go(-1)} />
          <IconButton icon={ChevronRightIcon} label={t('item.lightbox.next')} onClick={() => go(1)} />
        </div>
      )}
    </Dialog>
  );
}

export default Lightbox;
